import React from 'react';
import LegalLayout from './LegalLayout';

const Privacy = () => {
  return (
    <LegalLayout
      testId="legal-privacy-page"
      title="Privacy Policy"
      lastUpdated="February 2026"
      intro="This Privacy Policy explains how Daye Farm Ltd collects, uses, shares and protects your personal data in line with the Nigeria Data Protection Act (NDPA) 2023 and, where applicable, the EU General Data Protection Regulation (GDPR)."
    >
      <h2>1. Data Controller</h2>
      <p>
        Daye Farm Ltd (the "Data Controller") of 110 Ankpa Ayingba Road, Kogi, Nigeria is
        responsible for the personal data processed through this website and related services. For
        any privacy-related queries please contact our Data Protection Officer at{' '}
        <a href="mailto:privacy@dayefarmltd.com">privacy@dayefarmltd.com</a>.
      </p>

      <h2>2. Personal Data We Collect</h2>
      <ul>
        <li>
          <strong>Identity &amp; contact data:</strong> full name, email, phone number, dial code,
          delivery address.
        </li>
        <li>
          <strong>Account data:</strong> hashed password, role (customer, investor, wholesale,
          admin), preferences.
        </li>
        <li>
          <strong>Transaction data:</strong> orders, invoices, wholesale agreements, investment
          expressions of interest, payment status (we do not store full card numbers).
        </li>
        <li>
          <strong>Communications:</strong> live chat messages, support tickets, emails and any
          correspondence with our team.
        </li>
        <li>
          <strong>Technical data:</strong> IP address, browser type, device identifiers, pages
          visited, referrer URL, session and cookie identifiers.
        </li>
        <li>
          <strong>KYC / compliance data:</strong> for investor and wholesale accounts where
          required by law.
        </li>
      </ul>

      <h2>3. How We Collect Data</h2>
      <ul>
        <li>Directly from you (registration, checkout, contact forms, chat, uploads).</li>
        <li>Automatically through cookies and similar technologies (see our Cookie Notice).</li>
        <li>
          From third-party providers such as Google (authentication), payment processors and
          logistics partners.
        </li>
      </ul>

      <h2>4. Lawful Bases for Processing</h2>
      <p>We process your personal data on one or more of the following bases:</p>
      <ul>
        <li>
          <strong>Contractual necessity</strong> — to create your account, fulfil orders and
          provide the services you have requested.
        </li>
        <li>
          <strong>Consent</strong> — for marketing communications, non-essential cookies and
          optional features. You may withdraw consent at any time.
        </li>
        <li>
          <strong>Legal obligation</strong> — to comply with tax, anti-money-laundering and
          consumer-protection requirements.
        </li>
        <li>
          <strong>Legitimate interests</strong> — to secure our systems, prevent fraud and improve
          our services, balanced against your rights.
        </li>
      </ul>

      <h2>5. How We Use Your Data</h2>
      <ul>
        <li>Create and manage your account and authenticate sessions.</li>
        <li>Process orders, payments, deliveries and returns.</li>
        <li>Operate the wholesale, investment and live chat services.</li>
        <li>Provide customer support and respond to enquiries.</li>
        <li>Send transactional notifications and, with consent, marketing updates.</li>
        <li>Detect, prevent and investigate fraud, abuse or security incidents.</li>
        <li>Comply with applicable laws and regulatory requests.</li>
        <li>Analyse usage to improve our products, content and user experience.</li>
      </ul>

      <h2>6. Sharing Your Data</h2>
      <p>We do not sell your personal data. We share it only with:</p>
      <ul>
        <li>
          <strong>Service providers</strong> who process data on our behalf under written
          agreements — hosting, email, analytics, payment processing (e.g. Paystack), logistics and
          customer support tools.
        </li>
        <li>
          <strong>Authentication partners</strong> such as Google when you choose social sign-in.
        </li>
        <li>
          <strong>Regulators, courts and law enforcement</strong> where required by law.
        </li>
        <li>
          <strong>Business transferees</strong> in the event of a merger, acquisition or sale of
          assets, subject to equivalent privacy protections.
        </li>
      </ul>

      <h2>7. International Transfers</h2>
      <p>
        Some of our service providers may be located outside Nigeria. Where personal data is
        transferred internationally we rely on appropriate safeguards such as adequacy decisions,
        standard contractual clauses and equivalent measures recognised under the NDPA and GDPR.
      </p>

      <h2>8. Data Retention</h2>
      <p>
        We retain personal data only for as long as necessary for the purposes set out in this
        Policy, including to satisfy legal, accounting and reporting obligations. Typical retention
        windows are:
      </p>
      <ul>
        <li>Account data: while the account is active and up to 24 months after deactivation.</li>
        <li>Transaction records: 7 years (Nigerian tax and accounting requirements).</li>
        <li>Marketing data: until consent is withdrawn.</li>
        <li>Live chat transcripts: 24 hours after a chat is closed, then automatically deleted.</li>
      </ul>

      <h2>9. Your Rights</h2>
      <p>Under the NDPA, GDPR and other applicable laws you have the right to:</p>
      <ul>
        <li>Access the personal data we hold about you.</li>
        <li>Request rectification of inaccurate or incomplete data.</li>
        <li>Request erasure of your data ("right to be forgotten") subject to legal limits.</li>
        <li>Restrict or object to certain processing.</li>
        <li>Request portability of your data in a structured, machine-readable format.</li>
        <li>Withdraw consent at any time, without affecting prior lawful processing.</li>
        <li>
          Lodge a complaint with the Nigeria Data Protection Commission (NDPC) or your local data
          protection authority.
        </li>
      </ul>
      <p>
        To exercise any of these rights, email{' '}
        <a href="mailto:privacy@dayefarmltd.com">privacy@dayefarmltd.com</a>. We will respond within
        30 days.
      </p>

      <h2>10. Security</h2>
      <p>
        We implement technical and organisational measures including encryption in transit (HTTPS),
        secure password hashing, role-based access control, secure <code>httpOnly</code> session
        cookies, audit logging, regular backups and supplier due diligence. No system is perfectly
        secure, and we encourage you to use strong, unique passwords and to enable two-factor
        authentication where available.
      </p>

      <h2>11. Children's Privacy</h2>
      <p>
        Our services are not directed to children under 18. We do not knowingly collect personal
        data from children. If you believe a child has provided us with data, please contact us so
        we can delete it.
      </p>

      <h2>12. Cookies &amp; Tracking</h2>
      <p>
        We use cookies and similar technologies as described in our{' '}
        <a href="/legal/cookies">Cookie Notice</a>. You can adjust your preferences at any time
        through the cookie banner or the "Manage Cookies" link in the footer.
      </p>

      <h2>13. Changes to this Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Where changes are material we will
        notify you by email or through a prominent notice on the site before they take effect.
      </p>

      <h2>14. Contact</h2>
      <p>
        Data Protection Officer<br />
        Daye Farm Ltd<br />
        110 Ankpa Ayingba Road, Kogi, Nigeria<br />
        Email: <a href="mailto:privacy@dayefarmltd.com">privacy@dayefarmltd.com</a>
      </p>
    </LegalLayout>
  );
};

export default Privacy;
